import React from 'react';
import { useEditorStore } from '../../store/editorStore';
import { toPxRect, toNormRect, clampNormRect } from '../../lib/coords';
import FieldNodeV2 from './FieldNodeV2';

export const FIELD_TYPE_DRAG_MIME = 'application/x-field-type';

const OverlayStage = ({ pageNum, pageMetrics, scale }) => {
  const { fields, selectedFieldType, setSelectedFieldType, addField, selectedFieldIds } = useEditorStore();
  
  if (!pageMetrics) return null;
  
  // Calculate canvas size
  const canvasWidth = pageMetrics.width * scale;
  const canvasHeight = pageMetrics.height * scale;
  
  // Get fields for this page, sorted by z-index
  const pageFields = fields
    .filter((f) => f.page === pageNum)
    .sort((a, b) => (a.z || 0) - (b.z || 0));
  
  // Place a new field of the given type centred on a viewport point
  const placeField = (fieldType, clientX, clientY, targetEl) => {
    const rect = targetEl.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const clickY = clientY - rect.top;

    // Default field dimensions (in pixels)
    let defaultWidth = 150;
    let defaultHeight = 30;

    if (fieldType === 'signature') {
      defaultWidth = 200;
      defaultHeight = 60;
    } else if (fieldType === 'radio' || fieldType === 'checkbox-group') {
      defaultWidth = 80;  // Larger to fit 2 options
      defaultHeight = 80;
    } else if (fieldType === 'checkbox') {
      defaultWidth = 20;
      defaultHeight = 20;
    }
    
    // Convert to normalized coordinates
    const normRect = toNormRect(
      {
        left: clickX - defaultWidth / 2,
        top: clickY - defaultHeight / 2,
        width: defaultWidth,
        height: defaultHeight,
      },
      pageMetrics,
      scale
    );
    
    // Clamp to page bounds
    const clampedRect = clampNormRect(normRect);
    
    // Build field props based on type
    const fieldProps = {
      required: true,
      label: `${fieldType} ${fields.length + 1}`,
    };

    // Add default options for radio and checkbox-group fields (NOT single checkbox)
    if (fieldType === 'radio' || fieldType === 'checkbox-group') {
      fieldProps.layout = 'vertical';
      fieldProps.spacingPx = 8;
      fieldProps.freePosition = true;  // Allow free dragging (SignEasy style)
      fieldProps.options = [
        { 
          id: '1', 
          label: 'Option 1',  // Default label
          value: 'option1',
          x: 0.1,  // Left aligned
          y: 0.15  // Top position
        },
        { 
          id: '2', 
          label: 'Option 2',  // Default label
          value: 'option2',
          x: 0.1,  // Left aligned
          y: 0.5   // Below first option
        }
      ];
      fieldProps.defaultValue = fieldType === 'radio' ? null : [];
    }

    // Add field
    addField({
      type: fieldType,
      page: pageNum,
      x: clampedRect.x,
      y: clampedRect.y,
      w: clampedRect.w,
      h: clampedRect.h,
      props: fieldProps,
    });
  };

  // Handle click to place new field
  const handleOverlayClick = (e) => {
    if (!selectedFieldType) return;
    placeField(selectedFieldType, e.clientX, e.clientY, e.currentTarget);
  };

  // Handle drag-and-drop of a field type from the "Add Fields" panel
  const handleDragOver = (e) => {
    if (!Array.from(e.dataTransfer.types || []).includes(FIELD_TYPE_DRAG_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    const fieldType = e.dataTransfer.getData(FIELD_TYPE_DRAG_MIME);
    if (!fieldType) return;
    e.preventDefault();
    placeField(fieldType, e.clientX, e.clientY, e.currentTarget);
    setSelectedFieldType(fieldType);
  };

  return (
    <svg
      className="overlay-stage absolute top-0 left-0"
      style={{
        width: `${canvasWidth}px`,
        height: `${canvasHeight}px`,
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Clickable overlay for placing fields */}
      {selectedFieldType && (
        <rect
          className="pointer-events-auto cursor-crosshair"
          width="100%"
          height="100%"
          fill="rgba(59, 130, 246, 0.1)"
          onClick={handleOverlayClick}
        />
      )}
      
      {/* Render field nodes (sorted by z-index) */}
      {pageFields.map((field) => (
        <FieldNodeV2
          key={field.id}
          field={field}
          pageMetrics={pageMetrics}
          scale={scale}
          isSelected={selectedFieldIds.includes(field.id)}
        />
      ))}
    </svg>
  );
};

export default OverlayStage;
